import { canonicalStringify, parseCoasterProject, type CoasterProject } from '../schema/index.ts';
import {
  CommandHistory,
  createProjectCommand,
  type EditorSelectionState,
  type HistoryState,
} from './commands.ts';
import { validateTrackTopology, type TopologyReport } from './topology.ts';
import { reconcilePieceGroups } from './piece-groups.ts';

export type EditorTool = 'build' | 'scenery' | 'look' | 'check';
export type EditorMode = 'basic' | 'advanced';
export type EditorSelectionTarget = 'shape' | 'piece';
export type EditorOverlay =
  'none' | 'speed' | 'vertical_g' | 'lateral_g' | 'curvature' | 'clearance';
export type SaveState = 'clean' | 'editing' | 'saving' | 'saved' | 'error';

export interface EditorSessionSnapshot {
  readonly project: CoasterProject;
  readonly selection: EditorSelectionState;
  readonly activeTool: EditorTool;
  readonly editorMode: EditorMode;
  readonly selectionTarget: EditorSelectionTarget;
  readonly activeOverlay: EditorOverlay;
  readonly saveState: SaveState;
  readonly revision: number;
  readonly topology: TopologyReport;
  readonly history: HistoryState;
}

interface GestureState {
  readonly id: string;
  readonly label: string;
  readonly before: CoasterProject;
  readonly selectionBefore: EditorSelectionState;
  readonly mergeKey?: string;
}

const EMPTY_SELECTION: EditorSelectionState = {
  nodeIds: [],
  primaryNodeId: null,
  sectionId: null,
  sceneryIds: [],
  primarySceneryId: null,
};

export class EditorSession {
  private readonly listeners = new Set<(snapshot: EditorSessionSnapshot) => void>();
  private readonly history = new CommandHistory();
  private projectValue: CoasterProject;
  private selectionValue: EditorSelectionState = EMPTY_SELECTION;
  private activeToolValue: EditorTool = 'build';
  private editorModeValue: EditorMode = 'basic';
  private selectionTargetValue: EditorSelectionTarget = 'piece';
  private overlayValue: EditorOverlay = 'none';
  private saveStateValue: SaveState = 'clean';
  private revisionValue = 0;
  private gesture: GestureState | null = null;

  constructor(project: CoasterProject) {
    this.projectValue = structuredClone(parseCoasterProject(project));
  }

  subscribe(listener: (snapshot: EditorSessionSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot(): EditorSessionSnapshot {
    return {
      project: this.projectValue,
      selection: this.selectionValue,
      activeTool: this.activeToolValue,
      editorMode: this.editorModeValue,
      selectionTarget: this.selectionTargetValue,
      activeOverlay: this.overlayValue,
      saveState: this.saveStateValue,
      revision: this.revisionValue,
      topology: validateTrackTopology(this.projectValue),
      history: this.history.state,
    };
  }

  get project(): CoasterProject {
    return this.projectValue;
  }

  get selection(): EditorSelectionState {
    return this.selectionValue;
  }

  replaceProject(project: CoasterProject, selection: EditorSelectionState = EMPTY_SELECTION): void {
    this.cancelGesture();
    this.projectValue = structuredClone(parseCoasterProject(project));
    this.selectionValue = structuredClone(selection);
    this.history.clear();
    this.revisionValue += 1;
    this.saveStateValue = 'clean';
    this.emit();
  }

  execute(
    label: string,
    candidate: CoasterProject,
    selectionAfter: EditorSelectionState = this.selectionValue,
    mergeKey?: string,
  ): boolean {
    const parsed = parseCoasterProject(reconcilePieceGroups(candidate));
    if (canonicalStringify(parsed) === canonicalStringify(this.projectValue)) return false;
    const command = createProjectCommand({
      id: `command:${this.revisionValue + 1}:${Date.now()}`,
      label,
      before: this.projectValue,
      after: parsed,
      selectionBefore: this.selectionValue,
      selectionAfter,
      ...(mergeKey ? { mergeKey } : {}),
    });
    this.history.push(command);
    this.projectValue = structuredClone(parsed);
    this.selectionValue = structuredClone(selectionAfter);
    this.revisionValue += 1;
    this.saveStateValue = 'editing';
    this.emit();
    return true;
  }

  beginGesture(label: string, mergeKey?: string): void {
    if (this.gesture) return;
    this.gesture = {
      id: `gesture:${Date.now()}`,
      label,
      before: structuredClone(this.projectValue),
      selectionBefore: structuredClone(this.selectionValue),
      ...(mergeKey ? { mergeKey } : {}),
    };
  }

  preview(candidate: CoasterProject, selection = this.selectionValue): void {
    if (!this.gesture) this.beginGesture('Edit track');
    this.projectValue = structuredClone(parseCoasterProject(reconcilePieceGroups(candidate)));
    this.selectionValue = structuredClone(selection);
    this.revisionValue += 1;
    this.saveStateValue = 'editing';
    this.emit();
  }

  commitGesture(): boolean {
    const gesture = this.gesture;
    if (!gesture) return false;
    this.gesture = null;
    if (canonicalStringify(gesture.before) === canonicalStringify(this.projectValue)) return false;
    const command = createProjectCommand({
      id: gesture.id,
      label: gesture.label,
      before: gesture.before,
      after: this.projectValue,
      selectionBefore: gesture.selectionBefore,
      selectionAfter: this.selectionValue,
      ...(gesture.mergeKey ? { mergeKey: gesture.mergeKey } : {}),
    });
    this.history.push(command);
    this.emit();
    return true;
  }

  cancelGesture(): void {
    if (!this.gesture) return;
    this.projectValue = this.gesture.before;
    this.selectionValue = this.gesture.selectionBefore;
    this.gesture = null;
    this.revisionValue += 1;
    this.emit();
  }

  undo(): boolean {
    this.cancelGesture();
    const command = this.history.undo();
    if (!command) return false;
    this.projectValue = structuredClone(command.before);
    this.selectionValue = structuredClone(command.selectionBefore);
    this.revisionValue += 1;
    this.saveStateValue = 'editing';
    this.emit();
    return true;
  }

  redo(): boolean {
    this.cancelGesture();
    const command = this.history.redo();
    if (!command) return false;
    this.projectValue = structuredClone(command.after);
    this.selectionValue = structuredClone(command.selectionAfter);
    this.revisionValue += 1;
    this.saveStateValue = 'editing';
    this.emit();
    return true;
  }

  setSelection(selection: EditorSelectionState): void {
    this.selectionValue = structuredClone(selection);
    this.emit();
  }

  setTool(tool: EditorTool): void {
    this.activeToolValue = tool;
    this.emit();
  }

  setEditorMode(mode: EditorMode): void {
    this.editorModeValue = mode;
    this.emit();
  }

  setSelectionTarget(target: EditorSelectionTarget): void {
    this.selectionTargetValue = target;
    this.emit();
  }

  setOverlay(overlay: EditorOverlay): void {
    this.overlayValue = overlay;
    this.emit();
  }

  setSaveState(state: SaveState): void {
    this.saveStateValue = state;
    this.emit();
  }

  private emit(): void {
    const snapshot = this.snapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }
}
